import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import clsx from 'clsx';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconCode from '@/assets/icons/icon-source-code.svg?react';
import { ScrollArea } from '@/components/ui/scroll-area';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import NodeOpacitySettings from '@/components/inspector-fields/SliderField/NodeOpacitySettings';
import { useNode } from '@craftjs/core';
import type { EmbedProps } from '@builder4/shared/components/Embed';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { validateEmbedHtmlCode } from '@builder4/shared/utils/htmlSanitationHelpers';
import { useAppDispatch } from '@/store/hooks';
import { showToast } from '@/store/notifications/slice';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';

type EmbedSourceType = 'url' | 'html';

interface InspectorEmbedProps {
  className?: string;
  dark?: boolean;
  text?: string;
}

export const InspectorEmbed: React.FC<InspectorEmbedProps> = ({
  className,
  dark = false,
  text = 'Embed',
}) => {
  const dispatch = useAppDispatch();
  const {
    actions: { setProp },
    props,
    displayName,
  } = useNode((node) => ({
    props: node.data.props as EmbedProps,
    displayName: node.data.displayName,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  const sourceType = getBPProp(props, 'sourceType') || 'html';
  const url = getBPProp(props, 'url') || '';
  const htmlCode = getBPProp(props, 'htmlCode') || '';

  const [urlInput, setUrlInput] = useState(url);
  const [htmlInput, setHtmlInput] = useState(htmlCode);

  useEffect(() => {
    setUrlInput(url);
  }, [url]);

  useEffect(() => {
    setHtmlInput(htmlCode);
  }, [htmlCode]);

  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const sourceTypeRefs = useRef<Record<EmbedSourceType, HTMLButtonElement | null>>({
    url: null,
    html: null,
  });

  useLayoutEffect(() => {
    const activeEl = sourceTypeRefs.current[sourceType];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [sourceType]);

  const handleUrlSubmit = () => {
    const trimmedUrl = urlInput.trim();
    setProp((props: EmbedProps) => {
      setBPProp(props, 'url', trimmedUrl);
    }, 500);
  };

  const handleSourceTypeChange = (newType: EmbedSourceType) => {
    setIsAnimated(true);
    setProp((props: EmbedProps) => {
      setBPProp(props, 'sourceType', newType);
    }, 500);
  };

  const handleHtmlCodeSubmit = () => {
    const trimmedCode = htmlInput.trim();
    const validation = validateEmbedHtmlCode(trimmedCode);
    if (!validation.isValid) {
      dispatch(
        showToast({
          text: validation.error || 'HTML contains unsafe content.',
          state: 'error',
          icon: 'error',
        }),
      );
      setHtmlInput(htmlCode);
      return;
    }
    setProp((props: EmbedProps) => {
      setBPProp(props, 'htmlCode', trimmedCode);
    }, 500);
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
        { dark },
      )}
      data-testid="inspector-embed"
    >
      <InspectorHeader className="max-w-[240px] text-neutral-900" icon={IconCode} text={text} />
      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            state="default"
            data-qa-selector="inspector-embed-layout-element"
          />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Embed settings</p>
          <div className="flex w-full max-w-[216px] items-center justify-between gap-4">
            <p className="text-button-sm font-medium text-neutral-500">Type</p>
            <div className="relative flex h-8 w-[160px] items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
              <div
                className={clsx(
                  'absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0]',
                  isAnimated && 'transition-all duration-300',
                )}
                style={{
                  width: `${highlightStyle.width}px`,
                  transform: `translateX(${highlightStyle.left}px)`,
                }}
              >
                <p className="text-button-sm font-semibold capitalize">
                  {sourceType === 'url' ? 'URL' : 'HTML'}
                </p>
              </div>
              {(['url', 'html'] as const).map((type) => (
                <button
                  key={type}
                  ref={(el) => {
                    sourceTypeRefs.current[type] = el;
                  }}
                  onClick={() => handleSourceTypeChange(type)}
                  className="text-button-sm relative z-10 flex-1 cursor-pointer px-2 py-1.5 text-neutral-400 capitalize"
                  data-testid={`source-type-${type}`}
                  data-qa-selector={`inspector-embed-source-type-${type}`}
                >
                  {type === 'url' ? 'URL' : 'HTML'}
                </button>
              ))}
            </div>
          </div>

          {sourceType === 'url' && (
            <Input
              className="max-w-[216px]"
              placeholder="Input your URL here"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onBlur={handleUrlSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUrlSubmit();
              }}
              data-qa-selector="inspector-embed-url-input"
            />
          )}

          {sourceType === 'html' && (
            <Textarea
              className="max-w-[216px] resize-none"
              placeholder="Paste your HTML here"
              value={htmlInput}
              onChange={(e) => setHtmlInput(e.target.value)}
              onBlur={handleHtmlCodeSubmit}
              rows={4}
              data-qa-selector="inspector-embed-html-input"
            />
          )}

          <p className="text-xs text-neutral-500">
            Note: Embeds could cause problems with your page. Make sure to check your published page
            for any discrepancies.
          </p>

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeOpacitySettings data-qa-selector="inspector-embed-opacity" />

          <NodeColorField
            propertyName="backgroundColor"
            solidPropertyName="bgSolid"
            gradientPropertyName="bgGradient"
            gradientEnabled
            label="Fill"
            data-qa-selector="inspector-embed-fill"
          />

          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
            data-qa-selector="inspector-embed-stroke-color"
          />

          <NodeStrokeSettings data-qa-selector="inspector-embed-stroke" />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings />
        </div>
      </ScrollArea>
    </div>
  );
};
